import { Injectable } from '@angular/core';
import CSVLayer from '@arcgis/core/layers/CSVLayer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import WFSLayer from '@arcgis/core/layers/WFSLayer';
import IdentifyParameters from '@arcgis/core/rest/support/IdentifyParameters';
import { defaultIfEmpty, map, Observable, of, shareReplay, zip } from 'rxjs';
import {
  EsriUtils,
  QueryableLayer,
  SublayerTypes,
} from 'app/shared/utils/esri.utils';
import {
  geomFieldsIgnored,
  systemFieldTypesIgnored,
} from 'app/shared/constants/ignore-fields.constant';

export interface HitTestResultItemLike {
  graphic: __esri.Graphic;
  layer: QueryableLayer;
}

@Injectable()
export class IdentifyService {
  private _attachmentsMap = new Map<
    string,
    Map<string, Observable<__esri.AttachmentInfo[]>>
  >();

  hitTest(
    mapView: __esri.MapView,
    mapPoint: __esri.Point,
    layerList?: (__esri.Layer | SublayerTypes)[]
  ) {
    const scale = mapView.scale;

    const bufferSize = EsriUtils.convertPxToDistanceXCoords(mapView, 15);
    const geometryToSearch = EsriUtils.geodesicBuffer(mapPoint, bufferSize);

    let allLayers: (__esri.Layer | SublayerTypes)[];
    if (!layerList) {
      const queryableLayerClasses = [FeatureLayer, CSVLayer, WFSLayer];
      const identifyLayerClasses = [MapImageLayer];
      allLayers = mapView.map.allLayers
        .toArray()
        .filter((layer) => {
          return (
            queryableLayerClasses.some((x) => layer instanceof x) ||
            identifyLayerClasses.some((x) => layer instanceof x)
          );
        })
        .reverse();
    } else {
      allLayers = layerList;
    }

    const hitTestObservableList = allLayers
      .filter((layer) =>
        EsriUtils.isRecursiveLayerVisibleAndInScale(layer, scale)
      )
      .map((layerAux) => {
        let observable: Observable<HitTestResultItemLike[]>;

        if (layerAux instanceof MapImageLayer) {
          const layerIds = EsriUtils.getDeeperLayers([layerAux])
            ?.filter((x) =>
              EsriUtils.isRecursiveLayerVisibleAndInScale(x, scale)
            )
            ?.reverse()
            ?.map((x) => x.id) as number[];

          if (!layerIds?.length) {
            observable = of([]);
          } else {
            const parameters = new IdentifyParameters({
              geometry: geometryToSearch,
              tolerance: 0,
              width: mapView.width,
              height: mapView.height,
              layerOption: 'visible',
              layerIds,
              mapExtent: mapView.extent,
              spatialReference: mapView.spatialReference,
              returnGeometry: true,
              returnUnformattedValues: true
            });

            observable = EsriUtils.identify(layerAux.url, parameters).pipe(
              map(
                (res) =>
                  res?.results?.map<HitTestResultItemLike>((x) => {
                    const graphic = x.feature;
                    const layer = layerAux.findSublayerById(x.layerId);

                    return { graphic, layer };
                  }) ?? []
              )
            );
          }
        } else {
          const layer = layerAux as QueryableLayer;
          const query = layer.createQuery();
          query.geometry = geometryToSearch;

          const fieldNames = layer.fields
            ?.filter(
              (x) =>
                !geomFieldsIgnored.includes(x.name) &&
                !systemFieldTypesIgnored.includes(x.type)
            )
            ?.map((x) => x.name);

          if (fieldNames) {
            fieldNames.unshift(layer.objectIdField);
          }

          query.outFields = fieldNames ?? ['*'];
          query.returnGeometry = true;

          observable = EsriUtils.queryFeatures(layer, query).pipe(
            map((featureSet) => {
              return (
                featureSet?.features?.map<HitTestResultItemLike>((graphic) => {
                  return { graphic, layer };
                }) ?? []
              );
            })
          );
        }

        return observable;
      });

    return zip(hitTestObservableList).pipe(
      map((res) => res.flat()),
      defaultIfEmpty([] as HitTestResultItemLike[]),
      map((results) => {
        type LayerTypeAux = QueryableLayer;

        const mapAux = new Map<
          LayerTypeAux,
          {
            order: number;
            layer: LayerTypeAux;
            list: __esri.Graphic[];
          }
        >();

        let order = 0;
        for (const item of results) {
          let list = mapAux.get(item.layer)?.list;
          if (!list) {
            list = [];
            mapAux.set(item.layer, {
              order: ++order,
              layer: item.layer,
              list,
            });
          }

          list.push(item.graphic);
        }

        const auxList = Array.from(mapAux.values()).sort(
          (a, b) => a.order - b.order
        );

        return auxList;
      })
    );
  }

  getAttachments(layer: __esri.FeatureLayer, objectId: number | string) {
    objectId = String(objectId);
    const layerUrl = layer.url;
    let mapAux = this._attachmentsMap.get(layerUrl);
    if (!mapAux) {
      mapAux = new Map();
      this._attachmentsMap.set(layerUrl, mapAux);
    }

    let observable = mapAux.get(objectId);
    if (!observable) {
      observable = EsriUtils.queryAttachments(layer, {
        objectIds: [Number(objectId)],
      }).pipe(
        map((res) => {
          return Array.from(res.values()).flat();
        }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      );
      mapAux.set(objectId, observable);
    }

    return observable;
  }
}
